import { Component } from 'react'
import { isEqual } from 'lodash'

import * as wishlistsRepository from 'scripts/api/wishlists'
import WishlistsService from 'scripts/api/wishlists/wishlists'

import { userResourcesApi } from 'api'

import AddToWishlist from 'site/Wishlist/components/drawer/lists/AddToWishlist'

import { tracker } from 'utils/app'
import { hideSpinner, showSpinner } from 'utils/Spinner'

import {
  isLoggedIn,
  isAssistedSale,
  redirectToLogin,
  emitSuccessMessage,
  emitErrorsMessage
} from './utils'
import { WishlistProvider } from './context'

class WishlistProviderMethods extends Component {
  constructor(props) {
    super(props)

    this.state = {
      type: null,
      id: null,
      product: null,
      isLogged: false,
      isAssistedSale: false,
      savedItems: [],
      showDrawer: false,
      wishlists: []
    }
  }

  async componentDidMount() {
    await this.setIsLogged()
  }

  componentDidUpdate(prevProps, prevState) {
    const updatedSavedItems = !isEqual(
      this.state.savedItems.length,
      prevState.savedItems.length
    )

    if (updatedSavedItems) {
      WishlistsService.setStorage(this.state.savedItems)
    }
  }

  setIsLogged = async () => {
    const userResourcesApiRequest =
      await userResourcesApi.storedGetUserResources()

    this.setState(
      {
        isLogged: isLoggedIn(userResourcesApiRequest),
        isAssistedSale: isAssistedSale(userResourcesApiRequest)
      },
      () => this.getProductsInWishlist()
    )
  }

  isOnWishlist = id => {
    const { savedItems } = this.state

    return savedItems.some(itemId => String(itemId) === String(id))
  }

  getProductsInWishlist = async () => {
    const data = await WishlistsService.get(
      this.state.isLogged,
      this.state.isAssistedSale
    )

    if (data) {
      this.setState({
        savedItems: [...data]
      })
    }
  }

  removeSaved = id => {
    const items = [...this.state.savedItems]
    const index = items.findIndex(item => String(item) === String(id))

    if (index !== -1) {
      items.splice(index, 1)
      this.setState({ savedItems: items })
    }
  }

  sendAdd = async (action, successMessage, errorMessage) => {
    const { id, savedItems } = this.state
    showSpinner()

    try {
      const lists = await action

      if (lists.status !== 204) {
        hideSpinner()
        throw new Error('Cannot add to wishlists')
      }

      this.setState({
        showDrawer: false,
        savedItems: [...savedItems, id]
      })
      hideSpinner()
      emitSuccessMessage(successMessage)
    } catch (err) {
      hideSpinner()
      emitErrorsMessage(errorMessage)
    }
  }

  sendCreate = async (action, successMessage, errorMessage) => {
    const { id, savedItems } = this.state

    try {
      const lists = await action
      const isNotCreated = lists.status !== 201 && lists.status !== 200

      if (isNotCreated) {
        hideSpinner()
        throw new Error(
          `Cannot create wishlist, API returns status: ${lists.status}`
        )
      }

      this.setState({
        showDrawer: false,
        savedItems: [...savedItems, id]
      })
      hideSpinner()
      emitSuccessMessage(successMessage)
    } catch (err) {
      hideSpinner()
      emitErrorsMessage(errorMessage)
    }
  }

  sendRemove = async (action, successMessage, errorMessage) => {
    try {
      const lists = await action

      if (lists.status !== 204) {
        throw new Error('Cannot remove from wishlists')
      }

      emitSuccessMessage(successMessage)
    } catch (err) {
      emitErrorsMessage(errorMessage)
    }
  }

  productAdd = list => {
    tracker.addToWishlist(this.state.product)

    this.sendAdd(
      wishlistsRepository.addProduct(list.slug, {
        id: this.state.id
      }),
      `Produto adicionado a "${list.name}".`,
      'Não conseguimos adicionar esse produto, tente novamente.'
    )
  }

  productRemove = id => {
    this.sendRemove(
      wishlistsRepository.removeProductFromLists(id),
      'Produto deletado da lista de favoritos!',
      'Não foi possível deletar o produto. Tente novamente.'
    )
    this.removeSaved(id)
  }

  contentAdd = list => {
    this.sendAdd(
      wishlistsRepository.addContent(list.slug, this.state.id),
      `Conteúdo adicionado a "${list.name}"`,
      'Não conseguimos adicionar esse conteúdo, tente novamente.'
    )
  }

  contentRemove = id => {
    this.sendRemove(
      wishlistsRepository.removeContentFromLists(id),
      'Conteúdo deletado da lista de favoritos!',
      'Não foi possível deletar o conteúdo. Tente novamente.'
    )
    this.removeSaved(id)
  }

  handleOpen = async id => {
    showSpinner()

    try {
      const lists = await wishlistsRepository.lists()
      if (lists.status !== 200) {
        hideSpinner()
        throw new Error(
          `Cannot get wishlists, API returns status: ${lists.status}`
        )
      }

      this.setState(
        {
          showDrawer: true,
          id: id,
          wishlists: lists.data.data
        },
        hideSpinner
      )
    } catch (err) {
      hideSpinner()
      redirectToLogin()
    }
  }

  handleClose = () => {
    this.setState({ showDrawer: false })
  }

  handleRemove = (id, typeName) => {
    if (typeName === 'contents') {
      return this.contentRemove(id)
    }

    return this.productRemove(id)
  }

  handleAdd = selectedList => {
    if (this.state.type.name === 'contents') {
      return this.contentAdd(selectedList)
    }

    return this.productAdd(selectedList)
  }

  handleNewList = listData => {
    const { type, id } = this.state

    const hasProduct = type.name === 'products'

    const getType = () => {
      if (hasProduct) {
        return {
          products: [{ id }]
        }
      }
      return { contents: [id] }
    }

    const formatData = {
      name: listData.get('wishlist-name'),
      background_image: listData.get('thumb-wishlist'),
      ...getType()
    }

    showSpinner()

    if (hasProduct) {
      tracker.addToWishlist(this.state.product)
    }

    this.sendCreate(
      wishlistsRepository.createList(formatData),
      'Lista de favoritos criada com sucesso!',
      'Não conseguimos criar sua lista, tente novamente.'
    )
  }

  onPressHeart = async (data, type) => {
    const { id, product = null } = data
    const isChecked = this.isOnWishlist(id)

    this.setState({
      type: { name: type },
      id,
      product
    })

    isChecked ? this.handleRemove(id, type) : this.handleOpen(id)
  }

  render() {
    const { showDrawer, wishlists, savedItems } = this.state

    const { isOnWishlist, onPressHeart } = this

    const contextValue = {
      savedItems,
      isOnWishlist,
      onPressHeart
    }

    const AddToWishlistProps = {
      isOpenedDrawer: showDrawer,
      closeDrawer: this.handleClose,
      createWishlist: this.handleNewList,
      isNewList: false,
      wishlists: wishlists,
      addToList: this.handleAdd
    }

    return (
      <WishlistProvider value={contextValue}>
        <AddToWishlist {...AddToWishlistProps} />
        {this.props.children}
      </WishlistProvider>
    )
  }
}

WishlistProviderMethods.displayName = 'WishlistProviderMethods'

export const withWishlistProviderMethods = WrappedComponent => {
  const WithWishlistProviderMethods = props => (
    <WishlistProviderMethods>
      <WrappedComponent {...props} />
    </WishlistProviderMethods>
  )

  WithWishlistProviderMethods.displayName = 'withWishlistProviderMethods'

  return WithWishlistProviderMethods
}

export default WishlistProviderMethods
